import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Copy, Download, RotateCcw, CheckCircle, Briefcase, ArrowRight, Save, Loader2, FileText } from "lucide-react";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { ParsedJob, MetricQuestion, QuantificationState } from "@/pages/ResumeQuantification";

interface QuantificationResultsStepProps {
  parsedJobs: ParsedJob[];
  questions: Record<string, MetricQuestion[]>;
  answers: Record<string, Record<string, string>>;
  rewrittenBullets: Record<string, Array<{
    bulletIndex: number;
    original: string;
    rewritten: string;
    numberAdded: string | null;
  }>>;
  firstName: string;
  lastName: string;
  onStartOver: () => void;
}

export function QuantificationResultsStep({
  parsedJobs,
  questions,
  answers,
  rewrittenBullets,
  firstName,
  lastName,
  onStartOver,
}: QuantificationResultsStepProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Calculate stats
  const totalBullets = parsedJobs.reduce((sum, job) => sum + job.bullets.length, 0);
  const bulletsWithMetrics = Object.values(rewrittenBullets)
    .flat()
    .filter(b => b.numberAdded !== null).length;

  const copyToClipboard = async (text: string, section: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      toast({ title: "Copied to clipboard" });
      setTimeout(() => setCopiedSection(null), 2000);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const downloadAsText = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded successfully" });
  };

  const handleDownloadAnsweredQuestions = async () => {
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      const documentTitle = fullName ? `${fullName} - Completed Metrics Questions` : "Completed Metrics Questions";
      
      const sections: Paragraph[] = [
        new Paragraph({
          spacing: { after: 400 },
          children: [
            new TextRun({ text: documentTitle, bold: true, size: 32, color: "000000" }),
          ],
        }),
        new Paragraph({
          spacing: { after: 400 },
          children: [
            new TextRun({ text: "Your answered questions for resume quantification.", size: 24, color: "000000" }),
          ],
        }),
      ];

      parsedJobs.forEach((job) => {
        const jobQuestions = questions[job.id] || [];
        const jobAnswers = answers[job.id] || {};
        
        // Job header
        sections.push(
          new Paragraph({
            spacing: { before: 400, after: 200 },
            children: [
              new TextRun({ text: `${job.title} at ${job.company}`, bold: true, size: 28, color: "000000" }),
            ],
          }),
          new Paragraph({
            spacing: { after: 300 },
            children: [
              new TextRun({ text: job.dates, size: 24, color: "000000" }),
            ],
          })
        );

        // Questions with answers
        jobQuestions.forEach((q, index) => {
          const answer = jobAnswers[q.id] || "(Not answered)";
          sections.push(
            new Paragraph({
              spacing: { before: 200, after: 100 },
              children: [
                new TextRun({ text: `${index + 1}. ${q.question}`, size: 24, color: "000000" }),
              ],
            }),
            new Paragraph({
              spacing: { after: 300 },
              children: [
                new TextRun({ text: `Answer: `, bold: true, size: 24, color: "000000" }),
                new TextRun({ text: answer, size: 24, color: answer === "(Not answered)" ? "888888" : "000000" }),
              ],
            })
          );
        });
      });

      const doc = new Document({
        sections: [{
          properties: {},
          children: sections,
        }],
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = fullName.replace(/[^a-zA-Z0-9]/g, '_') || 'resume';
      a.download = `${safeName}_completed_metrics_questions.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({ title: "Downloaded!", description: "Your completed questions have been downloaded." });
    } catch (error) {
      console.error("Download error:", error);
      toast({ title: "Download failed", variant: "destructive" });
    }
  };

  const handleSave = async () => {
    if (!user) {
      toast({ title: "Please log in to save", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase.from('quantified_resumes').insert({
        user_id: user.id,
        session_name: `Quantified Resume - ${new Date().toLocaleDateString()}`,
        parsed_jobs: parsedJobs as unknown as Record<string, unknown>,
        questions: questions as unknown as Record<string, unknown>,
        answers: answers as unknown as Record<string, unknown>,
        rewritten_bullets: rewrittenBullets as unknown as Record<string, unknown>,
        bullets_quantified: bulletsWithMetrics,
        total_bullets: totalBullets,
      } as any);

      if (error) throw error;

      toast({ title: "Saved successfully!", description: "Your quantified resume has been saved." });
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  // Generate final resume text
  const generateFinalResume = () => {
    return parsedJobs.map(job => {
      const jobBullets = rewrittenBullets[job.id] || [];
      const bullets = jobBullets.map(b => `• ${b.rewritten}`).join('\n');
      return `${job.title} | ${job.company} | ${job.dates}\n${bullets}`;
    }).join('\n\n');
  };

  // Generate comparison text
  const generateComparison = () => {
    return parsedJobs.map(job => {
      const jobBullets = rewrittenBullets[job.id] || [];
      const comparisons = jobBullets.map((b, i) => {
        if (b.numberAdded) {
          return `[${i + 1}] BEFORE: ${b.original}\n    AFTER:  ${b.rewritten}\n    ADDED:  ${b.numberAdded}`;
        }
        return `[${i + 1}] UNCHANGED: ${b.original}`;
      }).join('\n\n');
      return `=== ${job.title} at ${job.company} ===\n\n${comparisons}`;
    }).join('\n\n---\n\n');
  };

  const finalResumeText = generateFinalResume();
  const comparisonText = generateComparison();

  return (
    <div className="space-y-8">
      {/* Success Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-semibold mb-2">Your Quantified Resume is Ready!</h2>
        <p className="text-muted-foreground">
          {bulletsWithMetrics} of {totalBullets} bullets enhanced with metrics
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 text-center">
          <div className="text-3xl font-bold text-primary mb-1">{bulletsWithMetrics}</div>
          <div className="text-sm text-muted-foreground">Bullets Quantified</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-3xl font-bold text-green-600 mb-1">
            {totalBullets > 0 ? Math.round((bulletsWithMetrics / totalBullets) * 100) : 0}%
          </div>
          <div className="text-sm text-muted-foreground">Enhancement Rate</div>
        </Card>
      </div>

      {/* Results Tabs */}
      <Tabs defaultValue="comparison" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="comparison">Before & After</TabsTrigger>
          <TabsTrigger value="final">Final Bullets</TabsTrigger>
        </TabsList>

        <TabsContent value="comparison" className="space-y-6 mt-6">
          {parsedJobs.map((job) => {
            const jobBullets = rewrittenBullets[job.id] || [];
            
            return (
              <Card key={job.id} className="p-6">
                <div className="flex items-start gap-3 mb-6 pb-4 border-b">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Briefcase className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{job.title}</h3>
                    <p className="text-sm text-muted-foreground">{job.company} • {job.dates}</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {jobBullets.map((bullet, index) => (
                    <div key={index} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">Bullet {index + 1}</Badge>
                        {bullet.numberAdded && (
                          <Badge className="bg-green-600 text-xs">+{bullet.numberAdded}</Badge>
                        )}
                      </div>
                      
                      {bullet.numberAdded ? (
                        <div className="grid md:grid-cols-2 gap-3">
                          <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                            <div className="text-xs font-medium text-red-600 mb-1">BEFORE</div>
                            <p className="text-sm">• {bullet.original}</p>
                          </div>
                          <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="text-xs font-medium text-green-600 mb-1">AFTER</div>
                            <p className="text-sm">• {bullet.rewritten}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">• {bullet.original}</p>
                          <p className="text-xs text-muted-foreground mt-1">No changes - no metrics provided</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => copyToClipboard(comparisonText, 'comparison')}
              className="flex-1"
            >
              <Copy className="h-4 w-4 mr-2" />
              {copiedSection === 'comparison' ? 'Copied!' : 'Copy Comparison'}
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadAsText(comparisonText, 'resume-comparison.txt')}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="final" className="space-y-6 mt-6">
          <Card className="p-6">
            <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">
              {finalResumeText}
            </pre>
          </Card>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => copyToClipboard(finalResumeText, 'final')}
              className="flex-1"
            >
              <Copy className="h-4 w-4 mr-2" />
              {copiedSection === 'final' ? 'Copied!' : 'Copy All'}
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadAsText(finalResumeText, 'quantified-resume.txt')}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Download Answered Questions */}
      <Card className="p-4 bg-primary/5 border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">Download Completed Questions</p>
              <p className="text-sm text-muted-foreground">Get a Word document with all your answered metrics questions</p>
            </div>
          </div>
          <Button onClick={handleDownloadAnsweredQuestions} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Download Word Doc
          </Button>
        </div>
      </Card>

      {/* Save & Start Over */}
      <div className="pt-6 border-t flex justify-center gap-4">
        <Button onClick={handleSave} disabled={isSaving || !user}>
          {isSaving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {isSaving ? "Saving..." : "Save to Library"}
        </Button>
        <Button variant="ghost" onClick={onStartOver}>
          <RotateCcw className="h-4 w-4 mr-2" />
          Start Over
        </Button>
      </div>
    </div>
  );
}
